import { z } from 'zod';
import { emailSchema, phoneSchema, softDeletableSchema, uuidSchema } from './common';

// Family — the tenant-isolation boundary. Every family-scoped entity
// references family_id and every RLS policy is keyed on it.

export const familyAddressSchema = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).optional(),
  city: z.string().trim().min(1).max(100),
  region: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().min(2).max(20),
  country: z.string().trim().length(2).toUpperCase().default('US'),
});
export type FamilyAddress = z.infer<typeof familyAddressSchema>;

export const familyInputSchema = z.object({
  primary_guardian_name: z.string().trim().min(1).max(200),
  primary_guardian_email: emailSchema,
  primary_guardian_phone: phoneSchema,
  // Optional on input. A blank value coalesces to the guardian email at the
  // write boundary (the DB column is NOT NULL), mirroring the CSV import which
  // sets billing_email = guardian email. Empty string is normalized to
  // undefined so a deliberately blank field is not rejected as "Invalid email".
  billing_email: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    emailSchema.optional(),
  ),
  address: familyAddressSchema,
  notes: z.string().trim().max(2000).optional(),
});
export type FamilyInput = z.infer<typeof familyInputSchema>;

export const familySchema = softDeletableSchema.merge(familyInputSchema).extend({
  // A stored family always has a billing email (defaulted to the guardian
  // email on insert), so the persisted-row shape requires it.
  billing_email: emailSchema,
  stripe_customer_id: z.string().nullable(),
});
export type Family = z.infer<typeof familySchema>;

// CSV import row schema for the bulk Jackrabbit/Excel migration.
// Permissive on input — we trim, coerce, and validate.
export const familyImportRowSchema = z.object({
  guardian_name: z.string().trim().min(1),
  email: emailSchema,
  phone: phoneSchema,
  address_line1: z.string().trim().min(1),
  address_line2: z.string().trim().optional().default(''),
  city: z.string().trim().min(1),
  region: z.string().trim().min(1),
  postal_code: z.string().trim().min(2),
  country: z.string().trim().length(2).toUpperCase().optional().default('US'),
});
export type FamilyImportRow = z.infer<typeof familyImportRowSchema>;

export function familyImportRowToInput(row: FamilyImportRow): FamilyInput {
  return {
    primary_guardian_name: row.guardian_name,
    primary_guardian_email: row.email,
    primary_guardian_phone: row.phone,
    billing_email: row.email,
    address: {
      line1: row.address_line1,
      line2: row.address_line2 || undefined,
      city: row.city,
      region: row.region,
      postalCode: row.postal_code,
      country: row.country,
    },
  };
}

export const familyIdParamSchema = z.object({ familyId: uuidSchema });
